import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { DEBOUNCE_TIME } from 'data/global';

type Config = {
  initialSearchString?: string;
  onSearch?: (searchString: string) => void;
};

export default function useDebounceSearch(config?: Config) {
  const { initialSearchString = '', onSearch } = config || {};

  const [searchString, setSearchString] = useState(initialSearchString);
  const [searchInputValue, setSearchInputValue] = useState(initialSearchString);
  const [isDebouncing, setIsDebouncing] = useState(false);

  const onDebounce = useMemo(
    () =>
      debounce((searchString: string) => {
        setSearchString(searchString);
        setIsDebouncing(false);
        onSearch?.(searchString);
      }, DEBOUNCE_TIME),
    [onSearch],
  );

  const onSearchInputChange = useCallback(
    (value: string) => {
      if (value) setIsDebouncing(true);

      setSearchInputValue(value);
      onDebounce(value);
    },
    [onDebounce],
  );

  const resetSearchString = useCallback(() => {
    setSearchString('');
    setSearchInputValue('');
    setIsDebouncing(false);
  }, []);

  useEffect(() => () => onDebounce.cancel(), [onDebounce]);

  useEffect(() => {
    if (initialSearchString) {
      onSearch?.(initialSearchString);
    }
  }, [initialSearchString, onSearch]);

  return {
    searchString,
    searchInputValue,
    isDebouncing,
    resetSearchString,
    onSearchInputChange,
  };
}
